import { Component, Inject, signal, WritableSignal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { TrialBannerService, TrialDetails } from '../trial-banner/trial-banner.service';
import { Observable, map, take, switchMap } from 'rxjs';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { StartTrialModalComponent } from './start-trial-modal/start-trial-modal.component';
import { NUM_DAYS_AFFILIATE_TRIAL, NUM_DAYS_TRIAL } from './shared';
import { ActivatedRoute } from '@angular/router';
import { PARTNER_ID_INJECTION_TOKEN } from '../core/constants';
import { AcquisitionDataService } from '@galaxy/partner';
import { toSignal } from '@angular/core/rxjs-interop';

const BANNER_PAGE_PREVIOUSLY_SEEN = 'banner-page-previously-seen';
@Component({
  selector: 'app-start-trial-banner',
  imports: [CommonModule, MatCardModule, MatButtonModule, TranslateModule],
  templateUrl: './start-trial-banner.component.html',
  styleUrl: './start-trial-banner.component.scss',
})
export class StartTrialBannerComponent {
  public numDaysTrial: WritableSignal<number> = signal(NUM_DAYS_TRIAL);
  // Show start trial banner when the user is NOT on trial and is on the free tier
  private trialDetails = toSignal(this.trialBannerService.getTrialDetails$(), {
    // Provide a sensible initial value to avoid dealing with `undefined`
    initialValue: { expired: false, onTrial: true, isFree: false } as TrialDetails,
  });

  private featureId = toSignal(this.route.params.pipe(map((params) => params.featureId)), { initialValue: '' });

  private isRestrictedFeature(): boolean {
    // Features that are restricted to trial users
    // We disabled these feature for trial users due to its susceptibility to abuse
    // These are currently hardcoded in the partner microservice
    const trialRestrictedFeatures = [
      'snapshot-widget',
      'marketplace-sell-products',
      'marketing-automation',
      'inbox-sms',
      'ai-voice',
    ];
    return trialRestrictedFeatures.includes(this.featureId());
  }

  public showStartTrialBanner = computed<boolean>(() => {
    const details = this.trialDetails();
    const pagePreviouslySeen = localStorage.getItem(BANNER_PAGE_PREVIOUSLY_SEEN) === 'true';

    // Return the result of the combined conditions
    return !details.expired && !details.onTrial && details.isFree && pagePreviouslySeen && !this.isRestrictedFeature();
  });
  constructor(
    private readonly trialBannerService: TrialBannerService,
    private readonly productAnalyticsService: ProductAnalyticsService,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly acquisitionDataService: AcquisitionDataService,
    @Inject(PARTNER_ID_INJECTION_TOKEN) private partnerId$: Observable<string>,
  ) {
    if (localStorage.getItem(BANNER_PAGE_PREVIOUSLY_SEEN) === null) {
      localStorage.setItem(BANNER_PAGE_PREVIOUSLY_SEEN, 'true');
    }
    this.partnerId$
      .pipe(
        switchMap((partnerId) => this.acquisitionDataService.getAcquisitionData(partnerId)),
        take(1),
      )
      .subscribe((acquisitionData) => {
        if (acquisitionData.affiliateId) {
          this.numDaysTrial.set(NUM_DAYS_AFFILIATE_TRIAL);
        }
      });
  }

  startTrial(): void {
    this.productAnalyticsService.trackEvent('start-trial', 'start-trial-banner', 'click');
    this.dialog.open(StartTrialModalComponent, { data: { numDaysTrial: this.numDaysTrial() } });
  }
}
